// ABOUTME: The tags a listing card carries (PJL): whether it fits the talent's week, a raised rate, the last place, and
// ABOUTME: weekend or holiday work. Each only when pro-core said so; a card from before PJL carries none of them.
import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { BoldChip, BoldChipColors } from 'components/BoldChip';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import { CardWeekFit, JobListingCard } from '../types';
import { weekendDaysLabel } from '../utils';

const FIT_COLOR: Record<CardWeekFit, BoldChipColors> = {
  FITS: 'success',
  PARTLY: 'warning',
  NO: 'neutral',
  NO_AVAILABILITY: 'info'
};

/** The week fit as a tag; also the details' header uses it. */
export const WeekFitTag = ({ fit }: { fit: CardWeekFit }) => (
  <BoldChip
    data-testid="job-listing-week-fit"
    data-fit={fit}
    variant="soft"
    size="small"
    color={FIT_COLOR[fit]}
    label={getTrans(`jobs.badge.fit.${fit}` as TranslationKey)}
  />
);

/** @param leading chips the page puts first in the row (the job page of an application or of my job). */
export const ListingBadges = ({
  card,
  leading
}: {
  card: Partial<JobListingCard>;
  leading?: ReactNode;
}) => {
  const days = weekendDaysLabel(card.weekendWork);
  // Without the days, the feed's badge alone still says the job asks for weekends.
  const weekend = days
    ? getTransWithParams('jobs.badge.weekend', { label: days })
    : card.weekendAsked && getTrans('jobs.badge.weekendAny');
  const hasAny =
    !!leading ||
    !!card.weekFit ||
    !!card.earningsRaised ||
    !!card.placesLeft ||
    !!weekend;
  if (!hasAny) return null;

  return (
    <Box
      data-testid="job-listing-badges"
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}
    >
      {leading}
      {card.placesLeft && (
        <BoldChip
          data-testid="job-listing-places-left"
          variant="soft"
          size="small"
          color="warning"
          label={getTransWithParams('jobs.badge.placesLeft', {
            count: card.placesLeft
          })}
        />
      )}
      {card.weekFit && <WeekFitTag fit={card.weekFit} />}
      {card.earningsRaised && (
        <BoldChip
          data-testid="job-listing-raised"
          variant="soft"
          size="small"
          color="success"
          label={getTrans('jobs.badge.raised')}
        />
      )}
      {weekend && (
        <BoldChip
          data-testid="job-listing-weekend"
          variant="soft"
          size="small"
          color="error"
          label={weekend}
        />
      )}
    </Box>
  );
};
